import { emptySplitApi } from "./empty-split-api";
import { Practice, PracticeBillingSettings } from "./types/clinics.type";
import {
  AccessiblePractice,
  AccessiblePracticesFilters,
  ExternalIntegration,
  UpdateExternalIntegrationsRequest
} from "./types/practices.type";

export const practicesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    updatePractice: builder.mutation<
      Practice,
      Partial<Practice> & { id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/practices/${id}/`,
        method: "PATCH",
        body: body
      }),
      invalidatesTags: (_, __, body) =>
        "form_rendering_defaults" in body || "force_sso_only" in body
          ? ["Clinics"]
          : []
    }),
    // Narrow, flag-gated read used only by the practice settings form.
    // force_sso_only is deliberately NOT in the shared CLINICS_QUERY: RestQL
    // 400s (FieldNotFound) on fields the backend doesn't expose yet, and the
    // shared query runs for every user on core screens — this endpoint keeps
    // the blast radius of a frontend-first deploy to this single request.
    getPracticeAuthSettings: builder.query<
      Pick<Practice, "id" | "force_sso_only">,
      number
    >({
      query: (id) => ({
        url: `/practices/${id}/?query={id,force_sso_only}`
      }),
      providesTags: ["Clinics"]
    }),
    // The practice's Stripe customer, read fresh by the Scribe purchase screen before it says billing
    // isn't set up (SN-19868). `auth.practice` is the login-time copy, and Suno support sets the
    // customer from another session. No tags: nothing invalidates it, so a lazy trigger is always
    // its own request. Fetched unasked on a click, so the screen handles a failure itself.
    getPracticeStripeCustomer: builder.query<
      Pick<Practice, "id" | "stripe_customer_id">,
      number
    >({
      query: (id) => ({
        url: `/practices/${id}/?query={id,stripe_customer_id}`,
        timeout: 8000
      }),
      extraOptions: { suppressGlobalErrorToast: true, maxRetries: 0 }
    }),
    updatePracticeLogo: builder.mutation<
      Practice,
      { id: number; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/practices/${id}/`,
        method: "PATCH",
        body: formData
      }),
      // Unconditional on purpose, unlike the sibling `updatePractice` above: the
      // logo is part of the shared CLINICS_QUERY (nested under `practice{...}`),
      // so every screen reading that query needs the refetch to show a change.
      invalidatesTags: ["Clinics"]
    }),
    getPracticeBillingSettings: builder.query<PracticeBillingSettings, void>({
      query: () => ({
        url: `/practice/billing-settings/`,
        method: "GET"
      })
    }),
    backfillEdocFolders: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      void
    >({
      query: () => ({
        url: `/practices/backfill-edoc-folders/`,
        method: "POST"
      })
    }),
    // Practices the current user can switch into (ENG-1458 / T3). Suno staff get
    // ALL practices, so this is server-searched and capped at `limit` — pair
    // with a debounced search input on the switcher. The switcher is search-only
    // (no pagination wired), so no `offset` is sent. Tolerates a flat array or a
    // DRF `{ results }` envelope.
    getAccessiblePractices: builder.query<
      AccessiblePractice[],
      AccessiblePracticesFilters | void
    >({
      query: (filters) => ({
        url: `/auth/users/me/accessible-practices/`,
        params: {
          search: filters?.search || undefined,
          limit: filters?.limit ?? 25
        }
      }),
      transformResponse: (
        response: AccessiblePractice[] | { results?: AccessiblePractice[] }
      ): AccessiblePractice[] =>
        Array.isArray(response) ? response : (response?.results ?? [])
    }),
    // External partner data feeds, with who last enabled/disabled each one.
    //
    // A dedicated endpoint rather than fields on the shared CLINICS_QUERY for two
    // reasons. The flags simply aren't in that query, so `selectCurrentPractice`
    // returns undefined for every one of them; and the "who/when" half needs an
    // audit lookup that every other consumer of the practice payload — it is
    // nested in every user payload — would otherwise pay for on each read.
    getExternalIntegrations: builder.query<ExternalIntegration[], number>({
      query: (id) => ({ url: `/practices/${id}/external-integrations/` }),
      providesTags: ["ExternalIntegrations"]
    }),
    // Suno-only on the backend; a non-Suno caller gets a 403. The UI disables the
    // switches rather than relying on that, but the server is the actual gate.
    updateExternalIntegrations: builder.mutation<
      ExternalIntegration[],
      UpdateExternalIntegrationsRequest
    >({
      query: ({ id, integrations }) => ({
        url: `/practices/${id}/external-integrations/`,
        method: "PATCH",
        body: { integrations }
      }),
      // The response IS the refreshed payload, so seed the cache with it instead of
      // invalidating and refetching. The backend overlays the change it just made
      // onto that payload's audit data — it cannot read its own audit event back
      // (ATOMIC_REQUESTS + easy-audit's transaction.on_commit), so without that
      // overlay the card would show "No recorded change" until a page reload.
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            practicesApi.util.updateQueryData(
              "getExternalIntegrations",
              id,
              () => data
            )
          );
        } catch {
          // A failed write leaves the cache alone; the component surfaces the error.
        }
      }
    })
    // NOTE: switch-practice is NOT an RTK Query endpoint — it must send
    // `credentials: "include"` so the backend's HttpOnly refresh_token cookie is
    // stored, which fetchBaseQuery can't do per-request. See
    // `use-switch-practice.hook.ts` for the credentialed direct fetch.
  })
});

const {
  useUpdatePracticeMutation,
  useUpdatePracticeLogoMutation,
  useBackfillEdocFoldersMutation,
  useGetPracticeAuthSettingsQuery,
  useLazyGetPracticeStripeCustomerQuery,
  useGetPracticeBillingSettingsQuery: useGetPracticeBillingSettingsQueryBase,
  useGetAccessiblePracticesQuery,
  useLazyGetAccessiblePracticesQuery,
  useGetExternalIntegrationsQuery,
  useUpdateExternalIntegrationsMutation
} = practicesApi;

const DEFAULT_PRACTICE_BILLING_SETTINGS: PracticeBillingSettings = {
  credit_card_payment_enabled: true,
  subscription_payment_enabled: true,
  terminal_payment_enabled: true,
  online_payment_enabled: true,
  allow_payment_save: true
};

export const useGetPracticeBillingSettingsQuery = ({
  skip = false
}: { skip?: boolean } = {}) =>
  useGetPracticeBillingSettingsQueryBase(undefined, {
    skip,
    selectFromResult: ({ data, error, ...rest }) => {
      const is404Error = error && "status" in error && error.status === 404;
      return {
        data: is404Error ? DEFAULT_PRACTICE_BILLING_SETTINGS : data,
        error: is404Error ? undefined : error,
        ...rest
      };
    }
  });

export {
  useUpdatePracticeMutation,
  useUpdatePracticeLogoMutation,
  useBackfillEdocFoldersMutation,
  useGetPracticeAuthSettingsQuery,
  useLazyGetPracticeStripeCustomerQuery,
  useGetAccessiblePracticesQuery,
  useLazyGetAccessiblePracticesQuery,
  useGetExternalIntegrationsQuery,
  useUpdateExternalIntegrationsMutation
};
